import { useEffect, useState } from 'react';

import { useApolloClient } from '@apollo/client';

import { listChargesQuery } from 'graphql-queries';
import { GroupType } from 'graphql-query-contracts';
import {
  Charge,
  ExternalEntityType,
} from 'lane-shared/domains/billingPayments/types';
import { convertToUUID } from 'uuid-encoding';

import { listedChargeId } from './pendingChargeFromDraft';

type Params = {
  charges: Charge[];
  submittedCharge?: Charge;
  channelId: string;
  externalEntityId: string;
  externalEntityType: ExternalEntityType;
};

const isSameId = (left: string, right: string) =>
  left.toLowerCase() === right.toLowerCase();

export function useReloadedSubmittedCharge({
  charges,
  submittedCharge,
  channelId,
  externalEntityId,
  externalEntityType,
}: Params) {
  const client = useApolloClient();
  const [reloadedCharge, setReloadedCharge] = useState<Charge>();
  const submittedChargeId = submittedCharge && listedChargeId(submittedCharge);

  useEffect(() => {
    setReloadedCharge(undefined);

    if (!submittedChargeId) {
      return undefined;
    }

    let isCancelled = false;

    const listCharges = async (): Promise<Charge[]> => {
      try {
        const { data } = await client.query({
          query: listChargesQuery,
          fetchPolicy: 'no-cache',
          variables: {
            listChargesRequest: {
              groupId: convertToUUID(channelId),
              groupType: GroupType.GroupTypeActivateChannel,
              chargesFilters: {
                externalEntityIds: [externalEntityId],
                externalEntityType,
              },
              isDraft: false,
            },
          },
        });

        return data?.accounts?.listCharges?.charges ?? [];
      } catch {
        return [];
      }
    };

    const reload = async () => {
      const listed = await listCharges();
      const found = listed.find(({ id }) => isSameId(id, submittedChargeId));

      if (found && !isCancelled) {
        setReloadedCharge(found);
      }
    };

    reload();

    return () => {
      isCancelled = true;
    };
  }, [
    client,
    submittedChargeId,
    channelId,
    externalEntityId,
    externalEntityType,
  ]);

  const shownCharge = reloadedCharge ?? submittedCharge;

  return shownCharge ? [shownCharge, ...charges] : charges;
}
